/* eslint-disable unicorn/filename-case -- kebab-case per docs/conventions/core.md:
   this file exports a function, not a React component. */
import type { RowData } from '@tanstack/react-table';

import type { TreeRow } from './DimeTreeTable.types.js';

/**
 * Every expandable row id in a row tree, walked recursively regardless of current expansion state.
 *
 * Must be called on `table.getCoreRowModel().rows`, not `table.getRowModel().rows` — a collapsed
 * row's descendants are absent from the latter, so an expand-all built on it would only ever open
 * one level deep instead of the whole tree.
 */
export function collectExpandableRowIds<TData extends RowData>(
  rows: readonly TreeRow<TData>[],
): string[] {
  const ids: string[] = [];
  for (const row of rows) {
    if (row.getCanExpand()) {
      ids.push(row.id);
      // Only recurse into children of a row that CAN itself expand — a getRowCanExpand veto
      // makes this row permanently un-openable, so any expandable descendant underneath it is
      // permanently invisible too. Adding those ids here would leave the expanded record with
      // truthy entries for nodes that can never be seen, making getIsSomeRowsExpanded() report a
      // false positive.
      if (row.subRows.length > 0) ids.push(...collectExpandableRowIds(row.subRows));
    }
  }
  return ids;
}
